<template>
  <div v-if="visitItem">
    <OverviewHandling v-if='isSurgeon' :visitItem="visitItem" @updateCommonVisitInfo="updateCommonVisitInfo($event)" @updateVisitItem="updateVisitItem($event)"/>
    <v-container class="px-xl-7" fluid>
      <v-row class="my-0 align-start">
        <v-col cols="12" lg="8" class="pr-lg-7 pt-7">
          <v-btn class="mb-5" color="primary" @click="showPatientInfo = !showPatientInfo" outlined depressed>
            Patient Info
            <v-icon right size="22" class="ml-2">{{ !showPatientInfo ? 'icon-chevron-down' : 'icon-chevron-up' }}</v-icon>
          </v-btn>
          <div v-if="showPatientInfo">
            <v-row class="my-0">
              <v-col cols="5" md="3" class="py-0">
                <v-text-field v-model="cprNumber" label="CPR-Nummer *" maxlength="6" @paste.prevent="cprPasteHandler($event); resetFormButtons()" @input="resetFormButtons" filled/>
              </v-col>
              <v-col cols="5" md="3" class="py-0">
                <v-text-field ref="cprAdd" v-model="cprNumberAdd" label=" " maxlength="4" @input="resetFormButtons" filled/>
              </v-col>
              <v-col cols="12" md="auto" class="py-0 mb-6 mb-md-0">
                <div v-if='isAdmin' class="d-flex justify-start mb-1">
                  <v-checkbox :class="['my-0', 'py-0']" v-model="patientDoesntHaveEboks" @change="resetFormButtons" hide-details>
                    <template v-slot:label>
                      Bruger ikke e-Boks
                    </template>
                  </v-checkbox>
                </div>
                <div :class="['d-flex', 'justify-start', { 'mt-md-4': !isAdmin }]">
                  <v-checkbox :class="['my-0', 'py-0']" v-model="testPatient" @change="resetFormButtons" hide-details>
                    <template v-slot:label>
                      Test henvisning
                    </template>
                  </v-checkbox>
                </div>
              </v-col>
            </v-row>
            <v-row class="my-0">
              <v-col cols="12" md="6" class="py-0">
                <v-text-field v-model="firstName" label="Fornavn *" @input="resetFormButtons" filled/>
              </v-col>
              <v-col cols="12" md="6" class="py-0">
                <v-text-field v-model="lastName" label="Efternavn *" @input="resetFormButtons" filled/>
              </v-col>
            </v-row>
            <v-row class="my-0">
              <v-col cols="12" md="6" class="py-0">
                <v-text-field v-model="address" label="Adresse" @input="resetFormButtons" filled/>
              </v-col>
              <v-col cols="12" md="3" class="py-0">
                <v-text-field v-model="zip" label="Postnr *" @input="resetFormButtons" filled/>
              </v-col>
              <v-col cols="12" md="3" class="py-0">
                <v-text-field v-model="city" label="Bynavn *" @input="resetFormButtons" filled/>
              </v-col>
            </v-row>
            <v-row class="my-0">
              <v-col cols="12" md="3" class="py-0">
                <v-text-field v-model="phoneNumber" label="Tlf" @input="resetFormButtons" filled/>
              </v-col>
              <v-col cols="12" md="3" class="py-0">
                <v-text-field v-model="mobilePhone" label="Mobil nr." @input="resetFormButtons" filled/>
              </v-col>
              <v-col cols="12" md="6" class="py-0">
                <v-text-field v-model="patientEmail" label="Patient Email" @input="resetFormButtons" filled/>
              </v-col>
            </v-row>
          </div>
          <v-form ref="patientVisitForm" v-model="formValid">
            <v-row class="my-0 mb-5">
              <template v-if="isAdmin">
                <v-col cols="12" md="4" class="py-0 mt-4">
                  <v-select v-model="visitType" :items="VISIT_TYPES_LIST" item-value="key" item-text="name" label="Henvisning type" dense hide-details/>
                </v-col>
                <v-col v-if="visitType === VISIT_TYPES.EDI_KEY" cols="12" md="3" class="py-0 mt-4">
                  <v-text-field v-model="externalId" label="EDI ID *" dense :rules="[rules.required, rules.digit]" @input="resetFormButtons"/>
                </v-col>
              </template>
              <v-col cols="12" md="3" class="py-0 mt-4">
                <v-text-field v-model="externalDoctorId" label="Tandlæge-id" dense :disabled="!isAdmin" @input="resetFormButtons"/>
              </v-col>
            </v-row>
            <v-row class="my-0 mb-5">
              <v-col cols="12" md="6" class="py-0 mb-4 mb-md-0">
                <div :class="[captionClasses]">Klinik / henviser</div>
                <div v-if="visitItem.doctorFullName" class="d-flex align-center">
                  <v-btn color='grey lighten-2' class="mr-3" depressed fab x-small @click="openEditDentistDialog">
                    <v-icon size="20">mdi-square-edit-outline</v-icon>
                  </v-btn>
                  <div>
                    <div class='text-body-1'>{{ visitItem.doctorFullName }}</div>
                    <div class='text-body-2'><strong>klinik:</strong> {{visitItem.doctorClinic}}</div>
                  </div>
                </div>
                <div v-else class="text-body-1 grey--text">Ingen data...</div>
              </v-col>
              <v-col cols="12" md="6" class="py-0">
                <div :class="[captionClasses]">Sygesikring Danmark</div>
                <div v-if="healthInsuranceType" class="text-body-1">{{ healthInsuranceType }}</div>
                <div v-else class="text-body-1">Nej</div>
              </v-col>
            </v-row>
            <v-row v-if='isAdmin' class="my-0 mb-5">
              <v-col cols="12" md="6" class="py-0 mb-4 mb-md-0">
                <div :class="[captionClasses]">EDI ansvarlig tandlæge / henviser</div>
                <div v-if="visitItem.ediResponsibleDentist" class="text-body-1">{{ visitItem.ediResponsibleDentist }}</div>
                <div v-else class="text-body-1 grey--text">Ingen data...</div>
              </v-col>
            </v-row>
            <v-row class="my-0 mb-5">
              <v-col cols="12" md="3" class="py-0 pr-md-6">
                <v-menu ref="dateMenu" v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y max-width="290px" min-width="290px">
                  <template v-slot:activator="{ on }">
                    <v-text-field :value="formattedDate" class="datepicker-input" append-icon="mdi-calendar" @click:append="dateMenu = true" label="Dato" readonly :disabled="!isAdmin" v-on="on"/>
                  </template>
                  <v-date-picker v-model="date" no-title @change="dateMenu = false; onDateChange()" color="primary lighten-0"/>
                </v-menu>
              </v-col>
              <v-col cols="5" md="2" class="py-0 px-md-1">
                <v-select
                  ref="hourSelect"
                  v-model="selectedHour"
                  :items="hourOptions"
                  label="Timer"
                  :disabled="!isAdmin || !date"
                  @change="onHourChange"
                  :menu-props="{ offsetY: true, bottom: true, maxHeight: 200 }"
                />
              </v-col>
              <v-col cols="auto" class="py-0 px-0 font-weight-bold" align-self="center">:</v-col>
              <v-col cols="5" md="2" class="py-0 px-md-1">
                <v-select
                  ref="minuteSelect"
                  v-model="selectedMinute"
                  :items="minuteOptions"
                  label="Minutter"
                  :disabled="!isAdmin || !date"
                  @change="onMinutesChange"
                  :menu-props="{ offsetY: true, bottom: true, maxHeight: 200 }"
                />
              </v-col>
              <v-col v-if='isAdmin && date' cols="auto" class="py-0 px-0 font-weight-bold" align-self="center">
                <v-btn icon @click="resetTime">
                  <v-icon size='18'>mdi-close</v-icon>
                </v-btn>
              </v-col>
              <v-col cols="12" md="3" class="py-0 pl-md-6">
                <v-text-field ref="durationField" label="Tidsplan" append-icon="mdi-timer-outline" v-model="operationDuration" @input="resetFormButtons" :disabled="!isAdmin"/>
              </v-col>
            </v-row>
            <v-row class="my-0 mb-5">
              <v-col cols="12" md="6" class="py-0">
                <v-select v-if="isAdmin" :items="treatmentTypes" item-text="name" item-value="id" label="Vælg behandlingstype" v-model="treatmentType" @change="resetFormButtons">
                  <template v-slot:item="{ item, on }">
                    <v-list-item v-on="on" v-show="item.id !== -100">
                      <span>{{ item.name }}</span>
                    </v-list-item>
                  </template>
                </v-select>
                <v-text-field v-else label="Vælg behandlingstype" :value="treatmentTypeValue" disabled/>
              </v-col>
              <v-col cols="12" md="6" class="py-0">
                <v-select v-if="isAdmin" :items="surgeons" item-text="fullName" item-value="id" label="Behandler" loader-height="1" v-model="surgeon" @change="resetFormButtons"/>
                <v-text-field v-else label="Specialist" :value="visitItem.surgeonFullName" disabled/>
              </v-col>
            </v-row>
            <div class='mb-10' v-if="isAdmin && visitItem.visitAppointments?.length">
              <v-row class="my-0">
                <v-col cols="12" md="6" class="py-0">
                  <v-btn depressed color='primary' @click="showVisitAppointments = !showVisitAppointments" outlined>
                    Behandlingsplan
                    <v-icon right size="22" class="ml-2">{{ !showVisitAppointments ? 'icon-chevron-down' : 'icon-chevron-up' }}</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-row class="my-0 mt-5" v-if="showVisitAppointments">
                <v-col cols="12" class="py-0">
                  <div class='visit-appointments-table'>
                    <table>
                      <thead>
                      <tr>
                        <th class="text-left">
                          Dato
                        </th>
                        <th class="text-left">
                          Behandlingstype
                        </th>
                        <th class="text-left">
                          Ekstern kode
                        </th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr
                        v-for="(item, index) in visitItem.visitAppointments"
                        :key="index"
                      >
                        <td>{{ visitAppointmentDate(item) }}</td>
                        <td>{{ item.treatment }}</td>
                        <td>{{ item.externalAppointmentCode }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </v-col>
              </v-row>
            </div>
            <v-row class="my-0 mb-5" v-if="isAdmin">
              <v-col cols="12" class="py-0">
                <v-textarea v-model="comment" @input="resetFormButtons" label="Bemærk" outlined :auto-grow="true" :rows="1"/>
              </v-col>
            </v-row>
            <v-row class="my-0 mb-5" v-if="commonVisitInfo.status === 8 && isAdmin">
              <v-col cols="12" class="py-0">
                <v-textarea v-model="additionalComment" @input="resetFormButtons" label="Ekstra kommentar til status" outlined :auto-grow="true" :rows="1"/>
              </v-col>
            </v-row>
            <v-row class="my-0">
              <v-col cols="12" md="6" class="py-0" v-if="isAdmin">
                <v-text-field v-model="price" @input="resetFormButtons" label="Pris"/>
              </v-col>
              <v-col cols="12" md="6" class="py-0">
                <div :class="[captionClasses]">Betalingst.:</div>
                <div class="text-body-1">{{ paymentType }}</div>
              </v-col>
            </v-row>
          </v-form>
        </v-col>
        <v-col cols="12" lg="4">
          <div class="pa-5" style="background: #0000000f; border-radius: 5px; border: 1px solid #ccc">
            <div class="mb-10">
              <div :class="[captionClasses]">Henvisningstype</div>
              <div v-if="kindOfVisit" class="text-body-1">{{ kindOfVisit }}</div>
              <div v-else class="text-body-1 grey--text">Ingen data...</div>
            </div>
            <div class="mb-10">
              <div :class="[captionClasses]">Journal/doc.:</div>
              <div v-if="visitItem.journalFiles && visitItem.journalFiles.length > 0">
                <div v-for="journalItem in visitItem.journalFiles">
                  <v-chip class="mb-1" color="grey lighten-1" @click="downloadJournal(visitItem.id, journalItem.journalId)">
                    <span class="text-truncate" style="max-width: 220px">{{ journalItem.fileName }}</span>
                  </v-chip>
                </div>
              </div>
              <div v-else class="text-body-1 grey--text">Ingen data...</div>
            </div>
            <div class="mb-10">
              <div :class="[captionClasses]">Godkendelse/Bevilling</div>
              <v-chip v-if="visitItem.appropriationFileName" color="grey lighten-1" @click="downloadAppropriation(visitItem.id)">
                <span class="text-truncate" style="max-width: 220px">{{ visitItem.appropriationFileName }}</span>
              </v-chip>
              <div v-else class="text-body-1 grey--text">Ingen data...</div>
            </div>
            <div class="mb-10">
              <span :class="[captionClasses]">DICOM filer</span>
              <OverviewXrayList :radiographFiles="DICOMFiles" :visitId="visitItem.id" @updateCommonVisitInfo="updateCommonVisitInfo($event)" @updateVisitItem="updateVisitItem($event)"/>
              <OverviewXrayUpload :visitItem="visitItem" @updateCommonVisitInfo="updateCommonVisitInfo($event)" @updateVisitItem="updateVisitItem($event)"/>
            </div>
            <div class="mb-10">
              <span :class="[captionClasses]">Billeder og filer</span>
              <OverviewXrayList :radiographFiles="imageFiles" :visitId="visitItem.id" @updateCommonVisitInfo="updateCommonVisitInfo($event)" @updateVisitItem="updateVisitItem($event)" :showDate="true"/>
              <OverviewXrayUpload :visitItem="visitItem" @updateCommonVisitInfo="updateCommonVisitInfo($event)" @updateVisitItem="updateVisitItem($event)"/>
            </div>
            <div class="mb-10">
              <div :class="[captionClasses]">Billeder Eksport Status</div>
              <div v-if="radiographExportStatus" class="text-body-1">{{ radiographExportStatus }}</div>
              <div v-else class="text-body-1 grey--text">Ingen data...</div>
            </div>
            <div v-if="isAdmin">
              <v-checkbox :class="['my-0', 'mb-3', 'py-0']" v-model="sendRadiographToPatient" @change="resetFormButtons" hide-details>
                <template v-slot:label>
                  Send røntgen til patient (m. ubrev)
                </template>
              </v-checkbox>
            </div>
            <div v-if="isAdmin">
              <v-checkbox :class="['my-0', 'py-0']" v-model="forceRadiographExport" @change="resetFormButtons" hide-details>
                <template v-slot:label>
                  Tving eksport af røntgenbilleder
                </template>
              </v-checkbox>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row v-if="isAdmin" class="my-0">
        <v-col v-if="visitItem.sendNotificationToDentist" cols="12" lg="auto" class="mr-lg-auto">
          <v-btn depressed :color="sendStatusColor" :loading="sendStatusLoading" :disabled="sendStatusLoading" @click="sendStatusToDentist">Send status til henviser</v-btn>
        </v-col>
        <v-col cols="12" lg="auto" class="ml-lg-auto">
          <v-btn depressed class="mr-2 my-2 my-lg-0" :color="updateVisitButtonColor" :loading="updateVisitLoading" :disabled="updateVisitLoading" @click="updateVisit">Gem ændringer</v-btn>
          <v-btn depressed outlined :color="regenerateButtonColor" :loading="regenerateLoading" :disabled="regenerateLoading" @click="regenerateVisitPdf">Regenerere Henvisning PDF</v-btn>
        </v-col>
      </v-row>
    </v-container>
    <v-container class="px-xl-7 pt-2" fluid>
      <template v-if="isAdmin">
        <v-divider class="mb-8"/>
        <OverviewCallUp :visitItem="visitItem" :patientDoesntHaveEboks='visitItem.patientDoesntHaveEboks' :patientEmail="visitItem.patientEmail" :patientMobilePhone='visitItem.patientMobilePhone' @updateCommonVisitInfo="updateCommonVisitInfo($event)" @updateVisitItem="updateVisitItem($event)"/>
      </template>
      <template v-if="isAdmin">
        <v-divider class="mb-8"/>
        <OverviewAppointment :patientDoesntHaveEboks='visitItem.patientDoesntHaveEboks' :patientEmail="visitItem.patientEmail" :visitItem="visitItem"/>
      </template>
      <template v-if="isAdmin">
        <v-divider class="mb-8"/>
        <OverviewPatientInstructions :visitItem="visitItem" :patientDoesntHaveEboks='visitItem.patientDoesntHaveEboks' :patientEmail="visitItem.patientEmail" :patientMobilePhone='visitItem.patientMobilePhone'/>
      </template>
      <template v-if="isAdmin">
        <v-divider class="mb-8"/>
        <OverviewEBoksInvoices :visitItem="visitItem"/>
      </template>
      <v-divider class="mb-8"/>
      <OverviewLetters :visitItem="visitItem" @updateVisitItem="updateVisitItem($event)"/>
      <v-divider class="mb-8"/>
      <OverviewMessages :visitItem="visitItem" @updateCommonVisitInfo="updateCommonVisitInfo($event)" @updateVisitItem="updateVisitItem($event)"/>
      <OverviewLogs v-if="isAdmin" :visitItem="visitItem"/>
    </v-container>
    <OverviewEditDentistDialog :dialog="dentistDialog" :visitItem="visitItem" @closeDialog="closeDentistDialog" @updateVisitItem="updateVisitItem($event)" @updateCommonVisitInfo="updateCommonVisitInfo($event)"/>
  </div>
  <div v-else>
    <v-progress-linear indeterminate color="blue-grey" :height="1"/>
  </div>
</template>

<script>
  import api from '../../api/api';
  import methods from '../../utils/methods';
  import HeadingBox from '../common/HeadingBox';
  import OverviewXrayUpload from './OverviewXrayUpload';
  import OverviewLogs from './OverviewLogs';
  import OverviewMessages from './OverviewMessages';
  import OverviewLetters from './OverviewLetters';
  import OverviewAppointment from './OverviewAppointment';
  import OverviewCallUp from './OverviewCallUp';
  import OverviewXrayList from './OverviewXrayList';
  import {COMMON_TYPES} from '@/constants/commonTypes';
  import OverviewEditDentistDialog from '@/components/overview/OverviewEditDentistDialog';
  import validationRules from '@/utils/validationRules';
  import {VISIT_TYPES, VISIT_TYPES_LIST} from '@/constants/visitTypes';
  import OverviewEBoksInvoices from '@/components/overview/OverviewEBoksInvoices.vue';
  import OverviewHandling from '@/components/overview/OverviewHandling.vue';
  import OverviewPatientInstructions from '@/components/overview/OverviewPatientInstructions.vue';

  export default {
    name: 'OverviewExpanded',

    components: {OverviewHandling, OverviewPatientInstructions, OverviewEBoksInvoices, OverviewEditDentistDialog, OverviewXrayList, OverviewCallUp, OverviewAppointment, OverviewLetters, OverviewMessages, OverviewLogs, OverviewXrayUpload, HeadingBox},

    props: {
      commonVisitInfo: {
        type: Object,
      },
    },

    data: function () {
      return {
        visitItem: null,
        VISIT_TYPES,
        VISIT_TYPES_LIST,
        formValid: false,
        rules: {...validationRules},
        captionClasses: [...COMMON_TYPES.CAPTION_CLASSES],
        dateMenu: false,
        sendStatusLoading: false,
        sendStatusColor: 'primary',
        regenerateLoading: false,
        regenerateButtonColor: 'primary',
        updateVisitLoading: false,
        updateVisitButtonColor: 'primary',
        showPatientInfo: false,
        showVisitAppointments: false,
        firstName: '',
        lastName: '',
        address: '',
        zip: '',
        city: '',
        mobilePhone: '',
        phoneNumber: '',
        patientEmail: '',
        testPatient: false,
        patientDoesntHaveEboks: false,
        cprNumber: '',
        cprNumberAdd: '',
        visitType: VISIT_TYPES.SIMPLE_KEY,
        externalId: '',
        externalDoctorId: '',
        date: null,
        selectedHour: null,
        selectedMinute: null,
        operationDuration: '',
        treatmentType: null,
        treatmentTypeValue: '',
        surgeon: null,
        comment: '',
        additionalComment: '',
        price: '',
        dentistDialog: false,
        sendRadiographToPatient: false,
        forceRadiographExport: false,
      };
    },

    mounted: async function () {
      let visitInfo = await api.getVisitInfo(this.commonVisitInfo.id);
      this.visitItem = visitInfo.data;
      this.setEditableValues(visitInfo.data);
    },

    beforeDestroy: function () {
      let data = {};
      data.buttonColor = this.updateVisitButtonColor;
      this.$emit('collapseVisit', data);
    },

    computed: {
      role: function () {
        return this.$store.state.role;
      },
      isAdmin: function () {
        return this.$store.getters.isAdmin;
      },
      isSurgeon: function () {
        return this.$store.getters.isSurgeon;
      },
      treatmentTypes: function () {
        return this.$store.state.treatmentTypes;
      },
      surgeons: function () {
        return this.$store.state.surgeons;
      },
      formattedDate: function () {
        if (this.date) {
          return methods.formatDate(this.date);
        } else {
          return '';
        }
      },
      hourOptions: function () {
        const standardHours = Array.from({length: 14}, (_, i) => ({
          text: (7 + i).toString().padStart(2, '0'),
          value: (7 + i).toString().padStart(2, '0')
        }));

        // Add current selected value if it's not in standard range
        if (this.selectedHour && !standardHours.find(h => h.value === this.selectedHour)) {
          standardHours.push({
            text: this.selectedHour,
            value: this.selectedHour
          });
          standardHours.sort((a, b) => parseInt(a.value) - parseInt(b.value));
        }

        return standardHours;
      },
      minuteOptions: function () {
        const standardMinutes = Array.from({length: 12}, (_, i) => ({
          text: (i * 5).toString().padStart(2, '0'),
          value: (i * 5).toString().padStart(2, '0')
        }));

        // Add current selected value if it's not in standard range
        if (this.selectedMinute && !standardMinutes.find(m => m.value === this.selectedMinute)) {
          standardMinutes.push({
            text: this.selectedMinute,
            value: this.selectedMinute
          });
          standardMinutes.sort((a, b) => parseInt(a.value) - parseInt(b.value));
        }

        return standardMinutes;
      },
      kindOfVisit: function () {
        switch (this.visitItem.referenceType) {
          case 0:
            return 'Almindelig';
          case 1:
            return 'Haste henvisning';
          default:
            return null;
        }
      },
      radiographExportStatus: function () {
        switch (this.visitItem.radiographExportStatus) {
          case null:
            return null;
          case 0:
            return 'Ingen data';
          case 1:
            return 'Planlagt';
          case 2:
            return 'Fejl i export - tjek format';
          case 3:
            return 'Arkiveret korrekt';
        }
      },
      healthInsuranceType: function () {
        switch (this.visitItem.healthInsuranceType) {
          case null:
            return null;
          case 0:
            return 'Basis';
          case 1:
            return 'Gruppe1';
          case 2:
            return 'Gruppe2';
          case 3:
            return 'Gruppe5';
        }
      },
      paymentType: function () {
        switch (this.visitItem.paymentType) {
          case 0:
            return 'Egenbet';
          case 1:
            return 'Forsikringssag';
          case 2:
            return 'Regionstandpleje';
          case 3:
            return 'BUT';
        }
      },
      imageFiles: function() {
        return this.visitItem.radiographFiles.filter(file => file.fileType !== 4) || []
      },
      DICOMFiles: function() {
        return this.visitItem.radiographFiles.filter(file => file.fileType === 4) || []
      }
    },

    methods: {
      downloadJournal: function (visitId, journalId) {
        methods.downloadURL(api.journalsFileUrl(visitId, journalId));
      },
      downloadAppropriation: function (visitId) {
        methods.downloadURL(api.appropriationFileUrl(visitId));
      },
      setExternalId: function (id) {
        this.visitType = VISIT_TYPES.EDI_KEY;
        this.externalId = id;
      },
      resetFormButtons: function () {
        this.updateVisitButtonColor !== 'primary' && (this.updateVisitButtonColor = 'primary');
        this.regenerateButtonColor !== 'primary' && (this.regenerateButtonColor = 'primary');
        this.sendStatusColor !== 'primary' && (this.sendStatusColor = 'primary');
      },
      setTreatmentTypeValue: function () {
        if (this.visitItem.treatmentType === null) return;
        if (this.visitItem.treatmentType === -100) return this.treatmentTypeValue = 'Unknown treatment';
        for (let index = 0; index < this.treatmentTypes.length; index++) {
          const element = this.treatmentTypes[index];
          if (element.id === this.visitItem.treatmentType) {
            this.treatmentTypeValue = element.name;
            break;
          }
        }
      },
      getDatepickerDate: function (date) {
        let dateArray = date.split('-');
        let simpleYear = dateArray[0];
        let simpleMonth = dateArray[1];
        let simpleDay = dateArray[2].split('T')[0];
        return `${simpleYear}-${simpleMonth}-${simpleDay}`;
      },
      onDateChange: function () {
        this.$nextTick(() => {
          this.$refs.hourSelect.focus();
          this.$refs.hourSelect.activateMenu();
        });
      },
      onHourChange: function () {
        this.updateTime();
        this.$nextTick(() => {
          this.$refs.minuteSelect.focus();
          this.$refs.minuteSelect.activateMenu();
        });
      },
      onMinutesChange: function () {
        this.updateTime();
        this.$nextTick(() => {
          this.$refs.durationField.focus();
        });
      },
      updateTime: function () {
        if (this.selectedHour === null) {
          this.selectedHour = '00';
        }
        if (this.selectedMinute === null) {
          this.selectedMinute = '00';
        }
        this.resetFormButtons();
      },
      resetTime: function () {
        this.date = null;
        this.selectedHour = null;
        this.selectedMinute = null;
      },
      sendStatusToDentist: async function () {
        try {
          this.sendStatusLoading = true;
          await api.sendStatusToDentist(this.visitItem.id);
          this.sendStatusLoading = false;
          this.sendStatusColor = 'success';
        } catch (e) {
          this.sendStatusLoading = false;
          this.sendStatusColor = 'error';
        }
      },
      regenerateVisitPdf: async function () {
        try {
          this.regenerateLoading = true;
          await api.visitPdf(this.visitItem.id, true);
          this.regenerateLoading = false;
          this.regenerateButtonColor = 'success';
        } catch (e) {
          this.regenerateLoading = false;
          this.regenerateButtonColor = 'error';
        }
      },
      setEditableValues: function (visitItem) {
        this.cprNumber = visitItem.cpr.slice(0, visitItem.cpr.indexOf('-'));
        this.cprNumberAdd = visitItem.cpr.slice(visitItem.cpr.indexOf('-') + 1);
        this.testPatient = visitItem.isTestVisit;
        this.patientDoesntHaveEboks = visitItem.patientDoesntHaveEboks || false;
        this.firstName = visitItem.patientFirstName ? visitItem.patientFirstName : '';
        this.lastName = visitItem.patientLastName ? visitItem.patientLastName : '';
        this.address = visitItem.patientAddress ? visitItem.patientAddress : '';
        this.zip = visitItem.patientZip ? visitItem.patientZip : '';
        this.city = visitItem.patientCity ? visitItem.patientCity : '';
        this.phoneNumber = visitItem.patientPhone ? visitItem.patientPhone : '';
        this.mobilePhone = visitItem.patientMobilePhone ? visitItem.patientMobilePhone : '';
        this.patientEmail = visitItem.patientEmail ? visitItem.patientEmail : '';
        visitItem.externalId && this.setExternalId(visitItem.externalId);
        this.externalDoctorId = visitItem.externalDoctorId ? visitItem.externalDoctorId : '';
        if (visitItem.operationDate) {
          this.date = this.getDatepickerDate(visitItem.operationDate);
          visitItem.operationTimeHours >= 0 && (this.selectedHour = visitItem.operationTimeHours.toString().padStart(2, '0'));
          visitItem.operationTimeMinutes >= 0 && (this.selectedMinute = visitItem.operationTimeMinutes.toString().padStart(2, '0'));
        } else {
          this.date = null;
          this.selectedHour = null;
          this.selectedMinute = null;
        }
        this.operationDuration = visitItem.operationDuration ? visitItem.operationDuration : '';
        this.treatmentType = visitItem.treatmentType >= 0 || visitItem.treatmentType === -100 ? visitItem.treatmentType : null;
        this.setTreatmentTypeValue();
        this.surgeon = visitItem.surgeonId ? visitItem.surgeonId : null;
        this.comment = visitItem.surgeonComment ? visitItem.surgeonComment : '';
        this.additionalComment = visitItem.notNeedTreatmentComment ? visitItem.notNeedTreatmentComment : '';
        this.price = visitItem.price ? visitItem.price : '';
        this.sendRadiographToPatient = visitItem.sendRadiographToPatient;
        this.forceRadiographExport = visitItem.forceRadiographExport;
      },
      updateVisit: async function () {
        if (!this.$refs.patientVisitForm.validate()) {
          methods.showGlobalSnackbar('error', 'Der er felter som mangler at blive udfyldt, tjek venligst og udfyld disse.');
          return
        }
        let newData = {};
        newData.makeTest = this.testPatient;
        newData.patientDoesntHaveEboks = this.patientDoesntHaveEboks;
        this.firstName && (newData.patientFirstName = this.firstName);
        this.lastName && (newData.patientLastName = this.lastName);
        this.address && (newData.patientAddress = this.address);
        this.zip && (newData.patientZip = this.zip);
        this.city && (newData.patientCity = this.city);
        this.phoneNumber && (newData.patientPhone = this.phoneNumber);
        this.mobilePhone && (newData.patientMobilePhone = this.mobilePhone);
        this.patientEmail && (newData.patientEmail = this.patientEmail);
        this.visitItem.id && (newData.visitId = this.visitItem.id);
        this.visitType === VISIT_TYPES.EDI_KEY && (newData.externalId = this.externalId);
        this.externalDoctorId && (newData.externalDoctorId = this.externalDoctorId);
        this.date ? (newData.operationDate = new Date(this.date).toISOString()) : (newData.operationDate = null);
        this.selectedHour ? (newData.operationTimeHours = parseInt(this.selectedHour)) : (newData.operationTimeHours = null);
        this.selectedMinute ? (newData.operationTimeMinutes = parseInt(this.selectedMinute)) : (newData.operationTimeMinutes = null);
        this.cprNumber && this.cprNumberAdd && (newData.patientCpr = `${this.cprNumber}-${this.cprNumberAdd}`);
        this.treatmentType !== null && (newData.treatmentType = this.treatmentType);
        this.operationDuration && (newData.operationDuration = this.operationDuration);
        this.price && (newData.price = this.price);
        if (this.surgeon) {
          for (let index = 0; index < this.surgeons.length; index++) {
            const element = this.surgeons[index];
            if (element.id === this.surgeon) {
              newData.surgeonId = this.surgeon;
              break;
            }
          }
        }
        this.comment && (newData.surgeonComment = this.comment);
        this.additionalComment && (newData.notNeedTreatmentComment = this.additionalComment);
        this.commonVisitInfo.isDeleteAttachments && (newData.isDeleteAttachments = this.commonVisitInfo.isDeleteAttachments);
        newData.status = this.commonVisitInfo.status;
        newData.sendRadiographToPatient = this.sendRadiographToPatient;
        newData.forceRadiographExport = this.forceRadiographExport;
        try {
          this.updateVisitLoading = true;
          await api.updateVisit(newData);
          let newVisit = await api.getVisitInfo(this.visitItem.id);
          this.visitItem = newVisit.data;
          this.setEditableValues(newVisit.data);
          this.updateVisitLoading = false;
          this.updateVisitButtonColor = 'success';
          this.$emit('updateCommonVisitInfo', newVisit.data);
        } catch (e) {
          this.updateVisitLoading = false;
          this.updateVisitButtonColor = 'error';
        }
      },
      updateCommonVisitInfo: function (data) {
        this.$emit('updateCommonVisitInfo', data);
      },
      updateVisitItem: function (data) {
        this.visitItem = data;
        this.setEditableValues(data);
      },
      openEditDentistDialog: function () {
        this.dentistDialog = true
      },
      closeDentistDialog: function() {
        this.dentistDialog = false
      },
      cprPasteHandler: function(event) {
        const clipboardData = event.clipboardData || window.clipboardData;
        const pastedData = clipboardData.getData('Text');
        if (pastedData?.length > 6) {
          if (pastedData.includes('-')) {
            this.cprNumber = pastedData.split('-')[0].slice(0, 6);
            this.cprNumberAdd = pastedData.split('-')[1].slice(0, 4);
          } else {
            this.cprNumber = pastedData.slice(0, 6);
            this.cprNumberAdd = pastedData.slice(6, 10);
          }
          this.$refs.cprAdd.focus();
        } else if (pastedData?.length === 6) {
          this.cprNumber = pastedData;
          this.$refs.cprAdd.focus();
        } else {
          this.cprNumber = pastedData;
        }
      },
      visitAppointmentDate: function (item) {
        return item.appointmentDate ? `${methods.getSimpleDate(item.appointmentDate)} ${methods.getSimpleTime(item.appointmentDate)}` : '--.--';
      },
    },
  };
</script>

<style lang='scss' scoped>
  .visit-appointments-table {
    line-height: 1.2;
    border-radius: 4px;
    background: none;
    border: thin solid #d3d5d7;
    color: black;

    table {
      width: 100%;
      border-spacing: 0;
      table-layout: fixed;

      tbody {
        display: table-row-group;
      }
    }

    th {
      border-bottom: thin solid #d3d5d7;
      padding: 5px 16px;
      height: 48px;
    }

    td {
      padding: 5px 16px;
      height: 48px;
    }

    tr:not(:last-child)>td {
      border-bottom: thin solid #d3d5d7;
    }
  }
</style>
